import React, { useEffect, useState, useCallback } from 'react';
import { api, moneyExact } from '../lib/api';
import { RefreshCw, X, Landmark, Wallet, CheckCircle2 } from 'lucide-react';

// Unified reconcile modal — updates IOLTA (QBO trust balance) + all 5 Profit
// First bank accounts (Relay figures) in a single admin action. One save,
// one timestamp per source. Designed for Joey's Monday-morning routine.

const ACCOUNTS = [
  { code: 'Income', label: 'Indie Income', hint: 'Checking' },
  { code: 'Profit', label: 'Indie Profit', hint: 'TAP10.CAP6' },
  { code: 'Owner',  label: 'Indie Owner Pay', hint: 'TAP10.CAP10' },
  { code: 'Tax',    label: 'Indie Tax', hint: 'TAP15.CAP11' },
  { code: 'OpEx',   label: 'Indie Opex', hint: 'TAP65.CAP73' },
];

const freshLabel = {
  fresh: 'FRESH', aging: 'AGING', stale: 'STALE', unknown: 'NOT SET',
};
const freshClass = {
  fresh: 'text-indie-evergreen', aging: 'text-indie-cobalt',
  stale: 'text-indie-neg-dark',  unknown: 'text-indie-ink/50',
};

export default function ReconcileBalancesModal({ onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  const [iolta, setIolta] = useState({ balance: '', current: null, freshness: null });
  const [bank, setBank] = useState(
    ACCOUNTS.reduce((acc, a) => ({ ...acc, [a.code]: '' }), {}),
  );
  const [bankMeta, setBankMeta] = useState({ current: {}, freshness: null });
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ioltaResp, runwayResp] = await Promise.all([
        api.get('/iolta'),
        api.get('/cash-runway'),
      ]);
      const mb = ioltaResp.data?.manual_balance;
      setIolta({
        balance: mb?.balance != null ? String(mb.balance) : '',
        current: mb?.balance,
        freshness: ioltaResp.data?.manual_balance_freshness,
      });
      const perAcct = runwayResp.data?.per_account_balances || {};
      setBank(ACCOUNTS.reduce((acc, a) => ({
        ...acc, [a.code]: perAcct[a.code] != null ? String(perAcct[a.code]) : '',
      }), {}));
      setBankMeta({
        current: perAcct,
        freshness: runwayResp.data?.manual_entry?.freshness,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load current balances.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const bankTotal = ACCOUNTS.reduce((sum, a) => {
    const n = parseFloat(bank[a.code]);
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);

  const save = async () => {
    setErr('');
    setSaving(true);
    try {
      const promises = [];

      // IOLTA payload if changed / provided.
      const ioltaVal = parseFloat(iolta.balance);
      if (iolta.balance !== '' && Number.isFinite(ioltaVal)) {
        if (ioltaVal < 0 || ioltaVal > 10_000_000) {
          throw new Error('IOLTA balance must be $0–$10M.');
        }
        promises.push(api.patch('/iolta/manual-balance', {
          balance: ioltaVal,
          note: note.trim() || null,
        }));
      }

      // Bank balances payload — include any account that has a value.
      const bankPayload = {};
      for (const a of ACCOUNTS) {
        const raw = bank[a.code];
        if (raw === '' || raw == null) continue;
        const n = parseFloat(raw);
        if (!Number.isFinite(n) || n < 0) {
          throw new Error(`Invalid balance for ${a.label}.`);
        }
        bankPayload[a.code] = n;
      }
      if (Object.keys(bankPayload).length > 0) {
        promises.push(api.patch('/cash-runway/balances', {
          balances: bankPayload,
          note: note.trim() || null,
        }));
      }

      if (promises.length === 0) {
        throw new Error('Enter at least one balance to save.');
      }

      await Promise.all(promises);
      setSavedFlash(true);
      onSaved?.();
      setTimeout(() => { onClose?.(); }, 900);
    } catch (e) {
      setErr(e?.message || e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="reconcile-modal-backdrop"
      className="fixed inset-0 z-50 bg-indie-navy/60 backdrop-blur-sm flex items-start justify-center p-4 md:p-8 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        data-testid="reconcile-modal"
        className="bg-indie-cream w-full max-w-3xl border-t-4 border-indie-navy shadow-xl my-4"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-indie-navy/10">
          <div>
            <div className="label-editorial">Reconcile balances</div>
            <div className="text-xl font-semibold text-indie-navy mt-1">Monday-morning refresh</div>
            <div className="text-[11px] text-indie-ink/60 mt-1">
              Enter what Relay and QuickBooks show right now. One timestamp per source.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="reconcile-close-btn"
            className="p-1.5 text-indie-ink/60 hover:text-indie-navy"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-sm text-indie-ink/60">Loading current balances…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* IOLTA */}
            <section>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <Landmark className="text-indie-cobalt" size={16} />
                  <div className="text-sm font-semibold text-indie-navy uppercase tracking-wider">IOLTA · QuickBooks 3209</div>
                </div>
                {iolta.freshness && (
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${freshClass[iolta.freshness.status]}`}>
                    {freshLabel[iolta.freshness.status]}
                    {iolta.freshness.days_ago != null && iolta.freshness.status !== 'unknown' && (
                      <span className="text-indie-ink/50 font-normal ml-1">
                        · {iolta.freshness.days_ago.toFixed(iolta.freshness.days_ago < 1 ? 1 : 0)}d ago
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl text-indie-navy num-tabular">$</span>
                <input
                  type="number" min="0" step="0.01"
                  value={iolta.balance}
                  onChange={(e) => setIolta({ ...iolta, balance: e.target.value })}
                  data-testid="reconcile-iolta-input"
                  placeholder="94234.64"
                  className="w-64 text-2xl font-semibold text-indie-navy border-b-2 border-indie-navy/40 focus:border-indie-navy focus:outline-none num-tabular bg-transparent"
                />
                {iolta.current != null && (
                  <span className="text-[11px] text-indie-ink/60 ml-2">
                    was {moneyExact(iolta.current)}
                  </span>
                )}
              </div>
            </section>

            {/* Bank balances */}
            <section>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Wallet className="text-indie-navy" size={16} />
                  <div className="text-sm font-semibold text-indie-navy uppercase tracking-wider">Relay · Profit First accounts</div>
                </div>
                {bankMeta.freshness && (
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${freshClass[bankMeta.freshness.status]}`}>
                    {freshLabel[bankMeta.freshness.status]}
                    {bankMeta.freshness.days_ago != null && bankMeta.freshness.status !== 'unknown' && (
                      <span className="text-indie-ink/50 font-normal ml-1">
                        · {bankMeta.freshness.days_ago.toFixed(bankMeta.freshness.days_ago < 1 ? 1 : 0)}d ago
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ACCOUNTS.map((a) => (
                  <div key={a.code} className="bg-white border border-indie-navy/10 px-3 py-2.5">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-wider mb-1">
                      <span className="text-indie-navy font-semibold">{a.label}</span>
                      <span className="text-indie-ink/50">{a.hint}</span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-indie-ink/60 text-sm">$</span>
                      <input
                        type="number" min="0" step="0.01"
                        value={bank[a.code]}
                        onChange={(e) => setBank({ ...bank, [a.code]: e.target.value })}
                        data-testid={`reconcile-bank-${a.code.toLowerCase()}`}
                        className="flex-1 text-lg font-semibold text-indie-navy border-b border-indie-navy/30 focus:border-indie-navy focus:outline-none num-tabular bg-transparent py-0.5"
                        placeholder="0.00"
                      />
                    </div>
                    {bankMeta.current[a.code] != null && (
                      <div className="text-[10px] text-indie-ink/50 mt-1">
                        was {moneyExact(bankMeta.current[a.code])}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-end gap-2 text-sm num-tabular">
                <span className="text-indie-ink/60 uppercase tracking-wider text-[10px]">Bank total</span>
                <span data-testid="reconcile-bank-total" className="text-indie-navy font-semibold">{moneyExact(bankTotal)}</span>
              </div>
            </section>

            {/* Note + save */}
            <section>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="reconcile-note"
                placeholder="Optional note (e.g., 'Weekly reconciliation — Mon 7/1')"
                className="w-full border border-indie-navy/30 px-3 py-2 text-sm"
                maxLength={140}
              />
            </section>

            {err && (
              <div data-testid="reconcile-error" className="text-sm text-indie-neg-dark border border-indie-neg-dark/30 bg-indie-neg-dark/[0.05] px-3 py-2">
                {err}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-indie-navy/10 bg-indie-cream/60">
          <button
            type="button"
            onClick={onClose}
            data-testid="reconcile-cancel-btn"
            className="text-xs uppercase tracking-wider text-indie-ink/70 hover:text-indie-navy px-3 py-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading || savedFlash}
            data-testid="reconcile-save-btn"
            className="inline-flex items-center gap-2 bg-indie-navy text-indie-yellow px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-60 hover:bg-indie-navy/90"
          >
            {savedFlash ? (<><CheckCircle2 size={14} /> Saved</>)
              : saving ? (<><RefreshCw size={14} className="animate-spin" /> Saving…</>)
              : (<><RefreshCw size={14} /> Save both</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
